/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Switch as SwitchButton,
} from 'antd';

import pic from '../../assets/loginpage-img-light.png';
import titleLogo_light from '../../assets/login-logo-light.svg';
import titleLogo_dark from '../../assets/login-logo-dark.svg';
import { ReactComponent as Eye } from '../../assets/eye-icon.svg';
import { ReactComponent as EyeDark } from '../../assets/eye-dark-icon.svg';
import { ReactComponent as Arrow } from '../../assets/arrow-up-right.svg';
import { ReactComponent as BankIDLogo } from '../../assets/bankId-logo.svg';

import { login } from '../../data/store/user/userActions';
import { emailRules } from '../../helpers/validation';

import TwoFactorPage from '../TwoFactorPage/TwoFactorPage';
import BankIDLogin from '../../components/BankIDLogin/BankIDLogin';

import ThemeTopSwitcher from '../../components/ThemeTopSwitcher/ThemeTopSwitcher';
import CustomPasswordInput from '../../components/CustomPasswordInput/CustomPasswordInput';
import Spinner from '../Model/Spiner/Sriner';

import './style.scss';

const LoginPage = ({ theme, setTheme }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { t } = useTranslation('');

  const [remember, setRemember] = useState(true);
  const { isLoading } = useSelector((state) => state.userReducer);

  const onChangeRemember = () => {
    setRemember(!remember);
  };

  const isValid = useMemo(() => {
    return true;
  }, []);

  const handleForgot = () => {
    history.push('/forgot-password');
  };
  const handleSignUp = () => {
    history.push('/sign-up');
  };
  const handleBankIDLogin = () => {
    history.push('/login/bankid');
  };

  const onSubmit = useCallback(
    (fields) => {
      const data = {
        email: fields?.email.trim(),
        password: fields?.password.trim(),
      };
      dispatch(login({ data, history, remember }));
      form.resetFields();
    },
    [history, remember, dispatch],
  );

  const handleThemeToggler = () => {
    const newTheme = theme === 'dark-theme' ? 'light-theme' : 'dark-theme';
    setTheme(newTheme);
  };

  return (
    <div className="login-wrapper">
      <div className="theme-switcher-wrapper">
        <ThemeTopSwitcher
          text={['DARK', 'LIGHT']}
          switcher={theme === 'dark-theme' ? 0 : 1}
          handleClick={handleThemeToggler}
        />
      </div>
      <div className="login-picture">
        <img src={pic} alt="login-picture" />
      </div>
      <Switch>
        <Route exact path={`${url}`}>
          <div className="login-form">
            <div className="login-form-title">
              <img
                src={theme !== 'dark-theme' ? titleLogo_light : titleLogo_dark}
                alt="title logo"
              />
            </div>
            <Form
              form={form}
              name="login"
              layout="vertical"
              onFinish={onSubmit}
              style={{ position: 'relative' }}
            >
              <Form.Item
                label={t('EMAIL')}
                name="email"
                required={false}
                rules={emailRules}
                disabled={isLoading}
              >
                <Input
                  key={theme}
                  className="login-input custom-input"
                  placeholder={t('ENTER_EMAIL')}
                  onPressEnter={() => form.submit()}
                />
              </Form.Item>
              <Form.Item label={t('PASSWORD')} name="password" required={false}>
                <CustomPasswordInput
                  className="login-input pass"
                  visible="true"
                  placeholder={t('ENTER_PASSWORD')}
                  onPressEnter={() => form.submit()}
                  iconRender={() =>
                    theme === 'dark-theme' ? <EyeDark /> : <Eye />
                  }
                />
              </Form.Item>
              <Row justify="space-between">
                <Col className="login-btn-wrapper">
                  <SwitchButton
                    defaultChecked
                    onChange={onChangeRemember}
                    checked={remember}
                    id="login-form-remember-switch"
                  />
                  <label htmlFor="login-form-remember-switch">
                    {t('REMEMBER_ME')}
                  </label>
                </Col>
                <Col className="login-btn-wrapper">
                  <Button
                    type="text"
                    className="forgor-btn"
                    icon={<Arrow />}
                    iconPosition={'end'}
                    onClick={handleForgot}
                  >
                    {t('FORGOT_YOUR_PASSWORD')}
                  </Button>
                </Col>
              </Row>
              <Row className="login-submit-btn-wrapper">
                <Button onClick={() => form.submit()} disabled={!isValid} block>
                  {t('LOG_IN')}
                </Button>
              </Row>
              <Divider className="login-divider" />
              <Row className="login-bankid-btn-wrapper">
                <Button onClick={handleBankIDLogin} icon={<BankIDLogo />} block>
                  {t('LOGIN_WITH')}
                </Button>
              </Row>
              <Row className="login-bottom-link">
                <span className="text">{t('DONT_HAVE_ACCOUNT')}</span>
                <span className="link" onClick={handleSignUp}>
                  {t('SIGN_UP_NOW')}
                </span>
                <Arrow />
              </Row>
              {isLoading && (
                <Spinner position="absolute" width="100%" height="100%" />
              )}
            </Form>
          </div>
        </Route>
        <Route exact path={`${url}/two-factor-auth`}>
          <TwoFactorPage remember={remember} theme={theme} />
        </Route>
        <Route exact path={`${url}/bankid`}>
          <BankIDLogin remember={remember} theme={theme} />
        </Route>
      </Switch>
    </div>
  );
};

export default LoginPage;

LoginPage.propTypes = {
  theme: PropTypes.string,
  setTheme: PropTypes.func,
};
